'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef } from 'react';
import { useWebHaptics } from 'web-haptics/react';
import dynamic from 'next/dynamic';
import { ClothingCard } from './ClothingCard';
import { GridScrollFade } from './GridScrollFade';

// three.js + gsap only load once carousel mode is actually opened
const LiquidCarouselView = dynamic(() => import('./LiquidCarouselView').then(mod => mod.LiquidCarouselView), {
  loading: () => null
});
const DeleteConfirmModal = dynamic(() => import('./DeleteConfirmModal').then(mod => mod.DeleteConfirmModal), {
  loading: () => null
});
import { deleteClosetItem } from '@/lib/closet/api-client';
import { transformClosetItem } from '@/lib/closet/transforms';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useCloset } from '@/contexts/ClosetContext';
import logger from '@/lib/logger';
import type { ClosetItem, ClothingItem } from '@/types/closet';
interface ClosetViewProps {
  searchQuery: string;
  selectedCategory: string;
  hoveredCategory?: string | null;
  onCategoryChange: (category: string) => void;
  layout: 'grid' | 'carousel';
  zoom: number;
  soundEnabled: boolean;
  playSound: (type: 'hover' | 'click' | 'switch' | 'success') => void;
  isVisible?: boolean;
  onLoad?: () => void;
}

// "accessories" is a combined bucket: it also covers fragrances.
function inCategory(item: ClosetItem, category: string): boolean {
  if (category === 'all') return true;
  if (category === 'accessories') {
    return item.category === 'accessories' || item.category === 'fragrances';
  }
  return item.category === category;
}
function matchesQuery(item: ClosetItem, query: string): boolean {
  return item.name.toLowerCase().includes(query) || item.category?.toLowerCase().includes(query) || item.brand?.toLowerCase().includes(query) || Boolean(item.color && item.color.toLowerCase().includes(query));
}
function filterClosetItems(items: ClosetItem[], selectedCategory: string, query: string | undefined): ClothingItem[] {
  return items.flatMap(item => {
    if (!inCategory(item, selectedCategory)) return [];
    if (query && !matchesQuery(item, query)) return [];
    return [transformClosetItem(item)];
  });
}

// Whether a card dims while a category is hovered in the sidebar.
function isDimmedByHover(item: ClothingItem, hoveredCategory: string | null | undefined): boolean {
  return hoveredCategory !== null && hoveredCategory !== undefined && !(hoveredCategory === 'accessories' && (item.category === 'accessories' || item.category === 'fragrances')) && item.category !== hoveredCategory;
}
interface ClothingGridProps {
  filteredItems: ClothingItem[];
  items: ClosetItem[];
  hoveredCategory: string | null | undefined;
  isMobile: boolean;
  zoom: number;
  onSelect: () => void;
  onDelete: (item: ClothingItem) => void;
}
function ClothingGrid(t0) {
  const $ = _c(23);
  const {
    filteredItems,
    items,
    hoveredCategory,
    isMobile,
    zoom,
    onSelect,
    onDelete
  } = t0;
  const trackWidth = isMobile ? 150 * (zoom / 100) : 200 * (zoom / 100);
  let t1;
  if ($[0] !== items) {
    t1 = new Set(items.flatMap(_temp));
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const processingIds = t1;
  const t2 = `px-4 ${isMobile ? "py-28" : "py-20"} sm:px-6 lg:px-8`;
  const t3 = `repeat(auto-fill, ${trackWidth}px)`;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      gridTemplateColumns: t3,
      justifyContent: "center"
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== filteredItems || $[5] !== hoveredCategory || $[6] !== onDelete || $[7] !== onSelect || $[8] !== processingIds || $[9] !== trackWidth) {
    let t6;
    if ($[11] !== hoveredCategory || $[12] !== onDelete || $[13] !== onSelect || $[14] !== processingIds || $[15] !== trackWidth) {
      t6 = (item_0, index) => <div key={item_0.id} style={{
        contentVisibility: "auto",
        containIntrinsicSize: `${trackWidth}px 300px`
      }}><ClothingCard item={item_0} index={index} isDimmed={isDimmedByHover(item_0, hoveredCategory)} onSelect={onSelect} onDelete={() => onDelete(item_0)} isProcessing={processingIds.has(item_0.id)} /></div>;
      $[11] = hoveredCategory;
      $[12] = onDelete;
      $[13] = onSelect;
      $[14] = processingIds;
      $[15] = trackWidth;
      $[16] = t6;
    } else {
      t6 = $[16];
    }
    t5 = filteredItems.map(t6);
    $[4] = filteredItems;
    $[5] = hoveredCategory;
    $[6] = onDelete;
    $[7] = onSelect;
    $[8] = processingIds;
    $[9] = trackWidth;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[17] !== t4 || $[18] !== t5) {
    t6 = <div className="grid gap-4" style={t4}>{t5}</div>;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== t2 || $[21] !== t6) {
    t7 = <div className="h-full overflow-y-auto"><div className={t2}>{t6}</div></div>;
    $[20] = t2;
    $[21] = t6;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  return t7;
}
function _temp(item) {
  return item.isProcessing ? [item.id] : [];
}
export function ClosetView(t0) {
  const $ = _c(45);
  const {
    searchQuery,
    selectedCategory,
    hoveredCategory,
    layout,
    zoom,
    playSound,
    isVisible: t1,
    onLoad
  } = t0;
  const isVisible = t1 === undefined ? true : t1;
  const haptic = useWebHaptics();
  const {
    items,
    isLoading,
    removeItem,
    refreshItems
  } = useCloset();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const isMobile = useIsMobile();
  const hasAttemptedLoadRef = useRef(false);
  let t2;
  let t3;
  if ($[0] !== isLoading || $[1] !== isVisible || $[2] !== items.length || $[3] !== onLoad || $[4] !== refreshItems) {
    t2 = () => {
      if (isVisible && !hasAttemptedLoadRef.current && items.length === 0 && !isLoading) {
        hasAttemptedLoadRef.current = true;
        refreshItems().then(() => {
          onLoad?.();
        });
      }
    };
    t3 = [isVisible, items.length, isLoading, refreshItems, onLoad];
    $[0] = isLoading;
    $[1] = isVisible;
    $[2] = items.length;
    $[3] = onLoad;
    $[4] = refreshItems;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== items || $[8] !== searchQuery || $[9] !== selectedCategory) {
    const query = searchQuery?.toLowerCase();
    t4 = filterClosetItems(items, selectedCategory, query);
    $[7] = items;
    $[8] = searchQuery;
    $[9] = selectedCategory;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const filteredItems = t4;
  let t5;
  if ($[11] !== playSound) {
    t5 = item => {
      setItemToDelete(() => item);
      setShowDeleteModal(true);
      playSound("click");
    };
    $[11] = playSound;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleDeleteClick = t5;
  let t6;
  if ($[13] !== playSound) {
    t6 = () => playSound("click");
    $[13] = playSound;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleCardSelect = t6;
  let t7;
  if ($[15] !== haptic || $[16] !== itemToDelete || $[17] !== playSound || $[18] !== removeItem) {
    t7 = async () => {
      if (!itemToDelete) {
        return;
      }
      setIsDeleting(true);
      setDeleteError("");
      ;
      try {
        await deleteClosetItem(itemToDelete.id);
        removeItem(itemToDelete.id);
        setShowDeleteModal(false);
        setItemToDelete(null);
        haptic.trigger("success");
        playSound("success");
      } catch (t8) {
        const error = t8;
        logger.error("Failed to delete item:", error);
        haptic.trigger("error");
        setDeleteError("COULD NOT DELETE. PLEASE TRY AGAIN.");
      }
      setIsDeleting(false);
    };
    $[15] = haptic;
    $[16] = itemToDelete;
    $[17] = playSound;
    $[18] = removeItem;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const handleConfirmDelete = t7;
  let t8;
  if ($[20] !== filteredItems || $[21] !== handleCardSelect || $[22] !== handleDeleteClick || $[23] !== hoveredCategory || $[24] !== isLoading || $[25] !== isMobile || $[26] !== items || $[27] !== layout || $[28] !== searchQuery || $[29] !== zoom) {
    t8 = isLoading ? <div className="flex h-full items-center justify-center"><p className="text-xs text-zinc-600 dark:text-zinc-400">LOADING…</p></div> : filteredItems.length > 0 ? layout === "grid" ? <ClothingGrid filteredItems={filteredItems} items={items} hoveredCategory={hoveredCategory} isMobile={isMobile} zoom={zoom} onSelect={handleCardSelect} onDelete={handleDeleteClick} /> : <LiquidCarouselView items={filteredItems} zoom={zoom} /> : <div className="flex h-full items-center justify-center"><p className="text-xs text-zinc-600 dark:text-zinc-400">{searchQuery ? "NO ITEMS MATCH THIS SEARCH." : "ARNE'S CLOSET IS EMPTY. COME BACK SOON."}</p></div>;
    $[20] = filteredItems;
    $[21] = handleCardSelect;
    $[22] = handleDeleteClick;
    $[23] = hoveredCategory;
    $[24] = isLoading;
    $[25] = isMobile;
    $[26] = items;
    $[27] = layout;
    $[28] = searchQuery;
    $[29] = zoom;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  let t9;
  if ($[31] !== filteredItems.length || $[32] !== layout) {
    t9 = filteredItems.length > 0 && layout === "grid" && <GridScrollFade />;
    $[31] = filteredItems.length;
    $[32] = layout;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setShowDeleteModal(false);
      setItemToDelete(null);
      setDeleteError("");
    };
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== deleteError || $[36] !== handleConfirmDelete || $[37] !== isDeleting || $[38] !== itemToDelete || $[39] !== showDeleteModal) {
    t11 = <DeleteConfirmModal item={itemToDelete} isOpen={showDeleteModal} onClose={t10} onConfirm={handleConfirmDelete} isDeleting={isDeleting} error={deleteError} />;
    $[35] = deleteError;
    $[36] = handleConfirmDelete;
    $[37] = isDeleting;
    $[38] = itemToDelete;
    $[39] = showDeleteModal;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] !== t11 || $[42] !== t8 || $[43] !== t9) {
    t12 = <div className="relative h-full">{t8}{t9}{t11}</div>;
    $[41] = t11;
    $[42] = t8;
    $[43] = t9;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  return t12;
}